/**
 * +grid: The Waystone Grid screen (docs/design/waystone-grid.md "UI"): constellations on a dark
 * starfield. Canvas 2D, drawn on demand (≈320 stars now, built for 1,200): stars are nodes, allocated
 * links glow gold, locks are sealed sigils, keystones large bright stars, each class gate a named
 * constellation. Pan (drag), zoom (wheel / buttons), search, hover tooltips, and a planning mode:
 * clicking a star plans the shortest route to it; Apply sends one `gridAllocate`, Cancel / Esc drops
 * the plan. Suggested previews the suggested path as a plan; Respec refunds everything.
 * Right-click an allocated star to refund it. The N key (route `talents`) opens this screen.
 */
import type { MetaScreenFactory } from '../context';
import { BASE_CLASSES, type GridNode, getGrid } from '@keystone/content';
import { gridSuggestion } from '@keystone/progression';
import { el } from '../../hud/dom';
import '../kmmo/kmmo.css';
import { kmConfirm, kmToast, primaryButton, screenFrame, secondaryButton } from '../kmmo';
import { type GridView, type NodeState, gridView, nodeState, planPathTo, planSummary, respecCost, searchGrid, tooltipFor, truncatePlan } from './gridModel';
import { GridArt, type Look, WORLD_R, fitRadius, hexA, screenRadius } from './gridArt';
import './grid.css';

interface Camera {
  x: number;
  y: number;
  zoom: number;
}

const ZOOM_MIN = 0.22;
const ZOOM_MAX = 3;
const GOLD = '#ffd36e';
const GOLD_GLOW = 'rgba(255, 196, 80, 0.55)';
const PLAN = '#8fe8ff';

const CLASS_COLOR: Readonly<Record<string, string>> = Object.fromEntries(BASE_CLASSES.map((c) => [c.id, c.color]));

/** Seeded starfield (deterministic sprinkle; the screen's backdrop). */
function starfield(w: number, h: number, dpr: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w * dpr));
  c.height = Math.max(1, Math.round(h * dpr));
  const g = c.getContext('2d')!;
  g.scale(dpr, dpr);
  const bg = g.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.75);
  bg.addColorStop(0, '#0b1230');
  bg.addColorStop(0.6, '#060a1c');
  bg.addColorStop(1, '#03050d');
  g.fillStyle = bg;
  g.fillRect(0, 0, w, h);
  let s = 0x2f6e2b1;
  const rnd = (): number => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
  const n = Math.round((w * h) / 1800);
  for (let i = 0; i < n; i++) {
    const x = rnd() * w;
    const y = rnd() * h;
    const r = rnd() < 0.92 ? 0.5 + rnd() * 0.7 : 1 + rnd() * 1.2;
    const a = 0.15 + rnd() * 0.55;
    g.fillStyle = rnd() < 0.15 ? `rgba(255, 220, 180, ${a})` : `rgba(200, 215, 255, ${a})`;
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fill();
  }
  return c;
}

export const gridScreen: MetaScreenFactory = (ctx) => {
  const store = ctx.store;
  const grid = getGrid();
  let view: GridView | null = gridView(store.profile);
  let plan: string[] = [];
  let hover: GridNode | null = null;
  let matches: string[] = [];
  let matchAt = 0;
  let busy = false;

  const f = screenFrame({
    title: 'Waystone Grid',
    onBack: () => ctx.router.back(),
    currencies: [{ id: 'gold', icon: 'gold', value: store.profile.currencies.gold ?? 0, title: 'Gold' }],
    className: 'kgrid',
  });
  const body = f.body;
  const canvas = el('canvas', 'kgrid-canvas', body) as HTMLCanvasElement;
  const g = canvas.getContext('2d')!;

  // ---- HUD -------------------------------------------------------------------
  const info = el('div', 'kgrid-info', body);
  const infoTitle = el('div', 'kgrid-info-title', info);
  const infoPoints = el('div', 'kgrid-info-row', info);
  const infoKeys = el('div', 'kgrid-info-row', info);

  const search = el('div', 'kgrid-search', body);
  const input = el('input', 'kgrid-search-input', search) as HTMLInputElement;
  input.type = 'search';
  input.placeholder = 'Search stars (crit, heal, Grindstone…)';
  input.setAttribute('aria-label', 'Search the Waystone Grid');
  const searchCount = el('span', 'kgrid-search-count', search);

  const tools = el('div', 'kgrid-tools', body);
  const suggestBtn = secondaryButton('Suggested', { size: 'sm', onClick: () => suggest() }, tools);
  suggestBtn.title = 'Preview the suggested path for your free points (then Apply)';
  const respecBtn = secondaryButton('Respec', { size: 'sm', onClick: () => respec() }, tools);
  const zoomOut = secondaryButton('−', { size: 'sm', onClick: () => zoomBy(1 / 1.3) }, tools);
  zoomOut.title = 'Zoom out';
  const zoomIn = secondaryButton('+', { size: 'sm', onClick: () => zoomBy(1.3) }, tools);
  zoomIn.title = 'Zoom in';
  const homeBtn = secondaryButton('My gate', { size: 'sm', onClick: () => centerOnGate(true) }, tools);
  homeBtn.title = 'Centre on your constellation';
  const wholeBtn = secondaryButton('Whole sky', { size: 'sm', onClick: () => fitAll() }, tools);
  wholeBtn.title = 'Show the whole grid';

  const planBar = el('div', 'kgrid-plan', body);
  const planText = el('div', 'kgrid-plan-text', planBar);
  const planCancel = secondaryButton('Cancel', { size: 'sm', onClick: () => setPlan([]) }, planBar);
  const planApply = primaryButton('Apply', { size: 'sm', onClick: () => apply() }, planBar);

  const legend = el('div', 'kgrid-legend', body);
  const sample = (type: GridNode['type']): GridNode => grid.nodes.find((n) => n.type === type)!;
  const legendSwatches: { canvas: HTMLCanvasElement; node: GridNode; look: Look }[] = [];
  for (const [cls, label, node, look] of [
    ['own', 'Allocated', sample('minor'), 'lit'],
    ['avail', 'Within reach', sample('minor'), 'avail'],
    ['plan', 'Planned', sample('minor'), 'plan'],
    ['notable', 'Notable', sample('notable'), 'avail'],
    ['slot', 'Ability star', sample('slot'), 'avail'],
    ['lock', 'Sealed sigil (Grid Key)', sample('lock'), 'needkey'],
    ['key', 'Keystone', sample('keystone'), 'avail'],
  ] as const) {
    const row = el('div', 'kgrid-legend-row', legend);
    const c = el('canvas', `kgrid-swatch ${cls}`, row) as HTMLCanvasElement;
    legendSwatches.push({ canvas: c, node, look });
    el('span', '', row, label);
  }

  const tip = el('div', 'kgrid-tip', body);
  tip.hidden = true;

  // ---- camera ------------------------------------------------------------------
  let w = 1;
  let h = 1;
  let dpr = 1;
  let bgCanvas: HTMLCanvasElement | null = null;
  const cam: Camera = { x: 0, y: 0, zoom: 0.8 };
  let placed = false;
  const toScreen = (x: number, y: number): [number, number] => [(x - cam.x) * cam.zoom + w / 2, (y - cam.y) * cam.zoom + h / 2];
  const toWorld = (sx: number, sy: number): [number, number] => [(sx - w / 2) / cam.zoom + cam.x, (sy - h / 2) / cam.zoom + cam.y];

  function resize(): void {
    const r = body.getBoundingClientRect();
    w = Math.max(1, r.width);
    h = Math.max(1, r.height);
    dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    bgCanvas = starfield(w, h, dpr);
    if (!placed && w > 2) {
      placed = true;
      centerOnGate(false);
    }
    dirty();
  }

  function centerOnGate(animate: boolean): void {
    const gate = view ? grid.byId.get(view.gate) : undefined;
    const fit = Math.min(w, h) / 700;
    const to = gate ? { x: gate.x * 1.35, y: gate.y * 1.35, zoom: Math.max(ZOOM_MIN, Math.min(1.4, fit)) } : { x: 0, y: 0, zoom: Math.min(w, h) / 1900 };
    moveTo(to, animate);
  }

  function fitAll(): void {
    moveTo({ x: 0, y: 0, zoom: Math.max(ZOOM_MIN, Math.min(w, h) / 1850) }, true);
  }

  let anim: { from: Camera; to: Camera; t0: number } | null = null;
  function moveTo(to: Camera, animate: boolean): void {
    if (!animate) {
      Object.assign(cam, to);
      dirty();
      return;
    }
    anim = { from: { ...cam }, to, t0: performance.now() };
    dirty();
  }

  function zoomBy(k: number, sx = w / 2, sy = h / 2): void {
    const [wx, wy] = toWorld(sx, sy);
    cam.zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, cam.zoom * k));
    // Keep the point under the cursor fixed.
    cam.x = wx - (sx - w / 2) / cam.zoom;
    cam.y = wy - (sy - h / 2) / cam.zoom;
    dirty();
  }

  // ---- drawing -----------------------------------------------------------------
  let raf = 0;
  let needs = true;
  function dirty(): void {
    needs = true;
  }

  // Node art (frames + painted icons): sprites cached per size step; icons load now (grid open).
  let legendStale = true;
  const art = new GridArt(() => {
    legendStale = true;
    dirty();
  });
  art.load(grid.nodes);
  /** Draw order: small stars first so frames of large ones sit on top. */
  const order = [...grid.nodes].sort((a, b) => WORLD_R[a.type] - WORLD_R[b.type]);

  const nodeRadius = (n: GridNode): number => screenRadius(n, cam.zoom);

  const on = (st: NodeState): boolean => st === 'own-gate' || st === 'allocated' || st === 'open';

  // States only change with the view or the plan: cache them per (view, plan).
  let states = new Map<string, NodeState>();
  let statesFor: { v: GridView | null; p: string[] } | null = null;
  function currentStates(): Map<string, NodeState> {
    if (statesFor && statesFor.v === view && statesFor.p === plan) return states;
    states = new Map();
    for (const n of grid.nodes) states.set(n.id, view ? nodeState(view, n, plan) : 'unreached');
    statesFor = { v: view, p: plan };
    return states;
  }

  /** A link: a ring arc around the centre when both ends sit on the same ring, else a straight line. */
  function seg(p: Path2D, n: GridNode, m: GridNode, ax: number, ay: number, bx: number, by: number, cx0: number, cy0: number): void {
    const ra = Math.hypot(n.x, n.y);
    const rb = Math.hypot(m.x, m.y);
    if (ra > 100 && Math.abs(ra - rb) < 3) {
      const a0 = Math.atan2(n.y, n.x);
      const a1 = Math.atan2(m.y, m.x);
      let d = a1 - a0;
      if (d > Math.PI) d -= Math.PI * 2;
      if (d < -Math.PI) d += Math.PI * 2;
      if (Math.abs(d) > 0.04 && Math.abs(d) < 1.3) {
        p.moveTo(ax, ay);
        p.arc(cx0, cy0, ((ra + rb) / 2) * cam.zoom, a0, a0 + d, d < 0);
        return;
      }
    }
    p.moveTo(ax, ay);
    p.lineTo(bx, by);
  }

  let drawMs = 0;
  function draw(now: number): void {
    const t0 = performance.now();
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (bgCanvas) g.drawImage(bgCanvas, 0, 0, w, h);
    const z = cam.zoom;
    const states = currentStates();
    const tw = 0.5 + 0.5 * Math.sin(now / 420);

    // Nebulae per class region and ring guides.
    for (const n of grid.nodes) {
      if (n.type !== 'gate' || !n.gate) continue;
      const [sx, sy] = toScreen(n.x * 1.7, n.y * 1.7);
      const r = 380 * z;
      const col = CLASS_COLOR[n.gate] ?? '#8899ff';
      const grad = g.createRadialGradient(sx, sy, 0, sx, sy, r);
      grad.addColorStop(0, hexA(col, n.gate === view?.classId ? 0.16 : 0.09));
      grad.addColorStop(1, hexA(col, 0));
      g.fillStyle = grad;
      g.fillRect(sx - r, sy - r, r * 2, r * 2);
    }
    const [cx0, cy0] = toScreen(0, 0);
    g.lineWidth = 1;
    for (const [r, label] of [
      [150, ''],
      [578, 'THE SEALS'],
      [834, 'THE OUTER DARK'],
    ] as const) {
      g.strokeStyle = 'rgba(150, 170, 230, 0.08)';
      g.setLineDash([4, 8]);
      g.beginPath();
      g.arc(cx0, cy0, r * z, 0, Math.PI * 2);
      g.stroke();
      g.setLineDash([]);
      if (label && z > 0.3 && z < 0.9) {
        g.fillStyle = 'rgba(170, 185, 235, 0.22)';
        g.font = `${Math.round(10 + 2 * z)}px Georgia, serif`;
        g.textAlign = 'left';
        g.fillText(label, cx0 + r * z + 8, cy0 + 4);
      }
    }

    // Links, batched by style: dim channels; allocated pairs glow gold; planned routes a moving cyan dash.
    const dim = new Path2D();
    const faint = new Path2D();
    const dormantP = new Path2D();
    const gold = new Path2D();
    const planP = new Path2D();
    for (const n of grid.nodes) {
      const sa = states.get(n.id)!;
      const [ax, ay] = toScreen(n.x, n.y);
      for (const l of n.links) {
        if (l < n.id) continue;
        const m = grid.byId.get(l)!;
        const sb = states.get(l)!;
        const [bx, by] = toScreen(m.x, m.y);
        if (Math.max(ax, bx) < -20 || Math.min(ax, bx) > w + 20 || Math.max(ay, by) < -20 || Math.min(ay, by) > h + 20) continue;
        const both = on(sa) && on(sb);
        const planned = (sa === 'planned' || on(sa)) && (sb === 'planned' || on(sb)) && (sa === 'planned' || sb === 'planned');
        const p = both ? gold : planned ? planP : sa === 'dormant' || sb === 'dormant' ? dormantP : sa === 'sealed' || sb === 'sealed' || sa === 'unreached' || sb === 'unreached' ? faint : dim;
        seg(p, n, m, ax, ay, bx, by, cx0, cy0);
      }
    }
    g.lineCap = 'round';
    // Dim channels: a dark groove with a thin steel line inside.
    g.strokeStyle = 'rgba(0, 0, 0, 0.55)';
    g.lineWidth = Math.max(2, 4 * z);
    g.stroke(faint);
    g.stroke(dim);
    g.strokeStyle = 'rgba(130, 145, 190, 0.28)';
    g.lineWidth = Math.max(0.8, 1.3 * z);
    g.stroke(faint);
    g.strokeStyle = 'rgba(190, 205, 245, 0.5)';
    g.stroke(dim);
    g.strokeStyle = 'rgba(120, 130, 170, 0.16)';
    g.setLineDash([3, 6]);
    g.stroke(dormantP);
    g.setLineDash([]);
    // Gold: wide soft glow, then a bright core.
    g.strokeStyle = 'rgba(255, 180, 60, 0.22)';
    g.lineWidth = Math.max(5, 10 * z);
    g.stroke(gold);
    g.strokeStyle = GOLD_GLOW;
    g.lineWidth = Math.max(2.6, 5 * z);
    g.stroke(gold);
    g.strokeStyle = GOLD;
    g.lineWidth = Math.max(1.4, 2.2 * z);
    g.stroke(gold);
    g.strokeStyle = 'rgba(255, 250, 225, 0.85)';
    g.lineWidth = Math.max(0.6, 0.8 * z);
    g.stroke(gold);
    // Planned: cyan glow + a dash that flows along the route.
    g.strokeStyle = 'rgba(120, 225, 255, 0.25)';
    g.lineWidth = Math.max(3, 6 * z);
    g.stroke(planP);
    g.strokeStyle = PLAN;
    g.lineWidth = Math.max(1.4, 2.2 * z);
    g.setLineDash([7, 6]);
    g.lineDashOffset = -now / 45;
    g.stroke(planP);
    g.setLineDash([]);
    g.lineDashOffset = 0;

    // Stars: framed icons from the sprite cache; live rims (within reach, seals you can open) on top.
    const matchSet = new Set(matches);
    const pulse = 0.5 + 0.5 * Math.sin(now / 300);
    const rims = new Path2D();
    for (const n of order) {
      const [sx, sy] = toScreen(n.x, n.y);
      if (sx < -60 || sx > w + 60 || sy < -60 || sy > h + 60) continue;
      const st = states.get(n.id)!;
      const look = lookOf(st);
      const hov = hover?.id === n.id;
      const r = nodeRadius(n) * (hov ? 1.22 : 1);
      art.draw(g, n, look, sx, sy, r, dpr);
      if (look === 'avail' && n.type !== 'gate') {
        const rr = r * (n.type === 'notable' ? 1.08 : n.type === 'keystone' ? 1.12 : 1.18) + 1.5 * pulse;
        rims.moveTo(sx + rr, sy);
        rims.arc(sx, sy, rr, 0, Math.PI * 2);
      }
      if (look === 'openable') {
        // Shimmer: two bright arcs circling the seal.
        const a = now / 500;
        g.strokeStyle = `rgba(255, 220, 140, ${0.55 + 0.4 * tw})`;
        g.lineWidth = Math.max(1.2, r * 0.14);
        g.beginPath();
        g.arc(sx, sy, r * 1.12, a, a + 1.1);
        g.stroke();
        g.beginPath();
        g.arc(sx, sy, r * 1.12, a + Math.PI, a + Math.PI + 1.1);
        g.stroke();
      }
      if (matchSet.has(n.id)) {
        g.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        g.lineWidth = 1.5;
        g.beginPath();
        g.arc(sx, sy, r * 1.35 + 4 + 2 * tw, 0, Math.PI * 2);
        g.stroke();
      }
    }
    g.strokeStyle = `rgba(235, 242, 255, ${0.45 + 0.4 * pulse})`;
    g.lineWidth = 1.4;
    g.stroke(rims);

    // Labels: constellations always, keystones and notables when close enough.
    g.textAlign = 'center';
    g.textBaseline = 'alphabetic';
    g.lineJoin = 'round';
    for (const n of grid.nodes) {
      const [sx, sy] = toScreen(n.x, n.y);
      if (sx < -120 || sx > w + 120 || sy < -60 || sy > h + 60) continue;
      if (n.type === 'gate' && n.gate) {
        // Constellation names: out past the region when the whole sky shows, else on a dark plate
        // just inside the gate (toward the Hearth Ring), clear of the star itself.
        const mine = n.gate === view?.classId;
        const far = z < 0.62;
        const title = n.name.toUpperCase();
        const sub = `${cap(n.gate)}${mine ? ' · you' : ''}`;
        const fs = Math.round(far ? 13 : 12 + 3 * Math.min(1.4, z));
        g.font = `600 ${fs}px Georgia, serif`;
        const tw0 = Math.max(g.measureText(title).width, 60);
        let lx: number;
        let ly: number;
        if (far) [lx, ly] = toScreen(n.x * 2.75, n.y * 2.75);
        else {
          // Just inside the gate (toward the Hearth Ring), pushed clear of the emblem's frame.
          const len = Math.hypot(n.x, n.y) || 1;
          const dx = -n.x / len;
          const dy = -n.y / len;
          const clear = nodeRadius(n) * 1.45 + 6;
          const d = Math.min(Math.abs(dx) > 0.01 ? (clear + tw0 / 2 + 8) / Math.abs(dx) : Infinity, Math.abs(dy) > 0.01 ? (clear + fs + 6) / Math.abs(dy) : Infinity);
          lx = sx + dx * d;
          ly = sy + dy * d - 3;
        }
        g.fillStyle = 'rgba(4, 7, 20, 0.62)';
        roundRect(lx - tw0 / 2 - 8, ly - fs - 2, tw0 + 16, fs * 2 + 10, 6);
        g.fillStyle = mine ? '#ffe7b0' : 'rgba(225, 230, 255, 0.9)';
        g.fillText(title, lx, ly);
        g.font = `${Math.round(fs * 0.8)}px system-ui, sans-serif`;
        g.fillStyle = mine ? 'rgba(255, 220, 150, 0.9)' : 'rgba(190, 200, 235, 0.7)';
        g.fillText(sub, lx, ly + fs);
      } else if (n.type === 'keystone' || (n.type === 'notable' && z >= 0.85) || (n.type === 'dormant' && z >= 1.2)) {
        const st = states.get(n.id)!;
        const fs = Math.round(10 + 2 * Math.min(1.4, z));
        g.font = `${n.type === 'keystone' ? '600 ' : n.type === 'notable' ? '500 ' : ''}${fs}px system-ui, sans-serif`;
        const ly = sy + nodeRadius(n) * (n.type === 'keystone' ? 1.6 : n.type === 'notable' ? 1.45 : 1) + fs + 2;
        g.strokeStyle = 'rgba(3, 5, 14, 0.85)';
        g.lineWidth = 3;
        g.strokeText(n.name, sx, ly);
        g.fillStyle = n.type === 'dormant' ? 'rgba(160, 170, 200, 0.55)' : on(st) ? '#ffe2a0' : n.type === 'keystone' ? '#fff1d0' : 'rgba(225, 232, 255, 0.85)';
        g.fillText(n.name, sx, ly);
      }
    }
    if (legendStale) drawLegend();
    drawMs = drawMs * 0.9 + (performance.now() - t0) * 0.1;
  }

  function roundRect(x: number, y: number, rw: number, rh: number, r: number): void {
    g.beginPath();
    g.moveTo(x + r, y);
    g.arcTo(x + rw, y, x + rw, y + rh, r);
    g.arcTo(x + rw, y + rh, x, y + rh, r);
    g.arcTo(x, y + rh, x, y, r);
    g.arcTo(x, y, x + rw, y, r);
    g.closePath();
    g.fill();
  }

  /** Legend swatches use the same node art as the sky. */
  function drawLegend(): void {
    legendStale = false;
    for (const s of legendSwatches) {
      const c = s.canvas;
      const px = 22;
      const d = Math.min(2, window.devicePixelRatio || 1);
      if (c.width !== px * d) {
        c.width = c.height = px * d;
        c.style.width = c.style.height = `${px}px`;
      }
      const cg = c.getContext('2d')!;
      cg.setTransform(d, 0, 0, d, 0, 0);
      cg.clearRect(0, 0, px, px);
      art.draw(cg, s.node, s.look, px / 2, px / 2, fitRadius(s.node, px), d);
    }
  }

  let lastDraw = 0;
  const frame = (now: number): void => {
    raf = requestAnimationFrame(frame);
    if (anim) {
      const t = Math.min(1, (now - anim.t0) / 420);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      cam.x = anim.from.x + (anim.to.x - anim.from.x) * e;
      cam.y = anim.from.y + (anim.to.y - anim.from.y) * e;
      cam.zoom = anim.from.zoom + (anim.to.zoom - anim.from.zoom) * e;
      if (t >= 1) anim = null;
      needs = true;
    }
    // Live parts (rim pulse when points are free, plan dash, seal shimmer, matches) at ~30 fps;
    // otherwise only when something changed.
    const live = !!view && (plan.length > 0 || view.points.free > 0 || view.keys.held > 0 || matches.length > 0);
    if (!needs && (!live || now - lastDraw < 33)) return;
    needs = false;
    lastDraw = now;
    draw(now);
  };

  // ---- HUD sync ---------------------------------------------------------------
  function syncHud(): void {
    view = gridView(store.profile);
    f.currencies?.set?.('gold', store.profile.currencies.gold ?? 0);
    if (!view) {
      infoTitle.textContent = 'No constellation';
      infoPoints.textContent = 'Create a hero to walk the grid.';
      infoKeys.textContent = '';
      return;
    }
    const gate = grid.byId.get(view.gate);
    infoTitle.textContent = ''; // simplified panel: points + keys only
    void gate;
    const pts = view.points;
    infoPoints.innerHTML = '';
    el('b', `kgrid-v${pts.free ? ' has' : ''}`, infoPoints, String(pts.free));
    el('span', 'kgrid-k', infoPoints, pts.free === 1 ? 'point' : 'points');
    infoKeys.innerHTML = '';
    el('b', `kgrid-v${view.keys.held ? ' has' : ''}`, infoKeys, String(view.keys.held));
    el('span', 'kgrid-k', infoKeys, view.keys.held === 1 ? 'key' : 'keys');
    const rc = respecCost(view);
    respecBtn.disabled = !view.allocated.size;
    respecBtn.title = view.allocated.size ? `Refund every star (${rc ? `${rc} gold` : 'free below level 10'}); opened seals stay open` : 'Nothing to refund';
    suggestBtn.disabled = !pts.free;
    syncPlan();
    dirty();
  }

  function syncPlan(): void {
    if (!view) return;
    const s = planSummary(view, plan);
    planBar.classList.toggle('show', plan.length > 0);
    planText.textContent = s.text;
    planText.classList.toggle('over', !s.ok && plan.length > 0);
    planApply.disabled = !s.ok || busy;
    planCancel.disabled = busy;
  }

  function setPlan(next: string[]): void {
    plan = next;
    syncPlan();
    dirty();
  }

  // ---- actions ------------------------------------------------------------------
  async function apply(): Promise<void> {
    if (!view || !plan.length || busy) return;
    busy = true;
    syncPlan();
    const r = await store.act('gridAllocate', { nodes: plan });
    busy = false;
    if (r.ok) {
      kmToast(`${plan.length} star${plan.length === 1 ? '' : 's'} allocated`, 'success');
      plan = [];
    } else kmToast(r.reason, 'error');
    syncHud();
  }

  function suggest(): void {
    const s = gridSuggestion(store.profile);
    if (!s.length) {
      kmToast('No points to spend', 'info');
      return;
    }
    setPlan(s);
    kmToast(`Suggested path: ${s.length} star${s.length === 1 ? '' : 's'} planned — Apply to keep them`, 'info', 3200);
  }

  function respec(): void {
    if (!view || !view.allocated.size) return;
    const cost = respecCost(view);
    kmConfirm(
      'Respec the Waystone Grid',
      `Refund all ${view.allocated.size} stars? ${cost ? `It costs ${cost} gold (2 per star).` : 'Free below level 10.'} Opened seals stay open.`,
      'Respec',
      () => {
        void store.act('gridRespec', {}).then((r) => {
          if (r.ok) kmToast('Every star refunded', 'success');
          else kmToast(r.reason, 'error');
          setPlan([]);
          syncHud();
        });
      },
      cost ? { cost: { icon: 'gold', amount: cost } } : {},
    );
  }

  function clickNode(n: GridNode): void {
    if (!view || busy) return;
    const st = nodeState(view, n, plan);
    if (st === 'planned') return setPlan(truncatePlan(plan, n.id));
    if (st === 'openable') {
      kmConfirm('Open the sealed sigil', `Spend a Grid Key to open this seal for good? (${view.keys.held} held)`, 'Open', () => {
        void store.act('gridUnlock', { lockId: n.id }).then((r) => {
          if (r.ok) kmToast('The seal opens', 'success');
          else kmToast(r.reason, 'error');
          syncHud();
        });
      });
      return;
    }
    if (st === 'needs-key') return void kmToast('You need a Grid Key to open this seal', 'info');
    if (st === 'available' || st === 'unreached') {
      const path = planPathTo(view, plan, n.id);
      if (!path) return void kmToast('Beyond a sealed sigil', 'info');
      setPlan([...plan, ...path]);
    }
  }

  function refundNode(n: GridNode): void {
    if (!view || busy || !view.allocated.has(n.id)) return;
    const t = tooltipFor(view, n, plan);
    if (t.note.includes('depend')) return void kmToast('Other stars depend on it', 'info');
    void store.act('gridRefund', { nodeId: n.id }).then((r) => {
      if (r.ok) kmToast(`${n.name} refunded`, 'success');
      else kmToast(r.reason, 'error');
      syncHud();
    });
  }

  // ---- input --------------------------------------------------------------------
  function pick(sx: number, sy: number): GridNode | null {
    let best: GridNode | null = null;
    let bd = Infinity;
    for (const n of grid.nodes) {
      const [x, y] = toScreen(n.x, n.y);
      const d = Math.hypot(x - sx, y - sy);
      const reach = Math.max(9, nodeRadius(n) * (n.type === 'notable' || n.type === 'keystone' ? 1.2 : 1) + 4);
      if (d < reach && d < bd) {
        bd = d;
        best = n;
      }
    }
    return best;
  }

  let drag: { x: number; y: number; cx: number; cy: number; moved: boolean; id: number } | null = null;
  const pointers = new Map<number, { x: number; y: number }>();
  let pinch: { d: number; zoom: number } | null = null;
  const local = (e: PointerEvent | WheelEvent | MouseEvent): [number, number] => {
    const r = canvas.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  canvas.addEventListener('pointerdown', (e) => {
    const [x, y] = local(e);
    pointers.set(e.pointerId, { x, y });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinch = { d: Math.hypot(a!.x - b!.x, a!.y - b!.y), zoom: cam.zoom };
      drag = null;
      return;
    }
    if (e.button !== 0) return;
    drag = { x, y, cx: cam.x, cy: cam.y, moved: false, id: e.pointerId };
    canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener('pointermove', (e) => {
    const [x, y] = local(e);
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x, y });
    if (pinch && pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      const d = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      cam.zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, (pinch.zoom * d) / Math.max(1, pinch.d)));
      dirty();
      return;
    }
    if (drag && drag.id === e.pointerId) {
      const dx = x - drag.x;
      const dy = y - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) > 4) drag.moved = true;
      if (drag.moved) {
        anim = null;
        cam.x = drag.cx - dx / cam.zoom;
        cam.y = drag.cy - dy / cam.zoom;
        tip.hidden = true;
        dirty();
        return;
      }
    }
    const n = pick(x, y);
    if (n !== hover) {
      hover = n;
      dirty();
    }
    showTip(n, x, y);
  });
  const endPointer = (e: PointerEvent): void => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = null;
    if (!drag || drag.id !== e.pointerId) return;
    const moved = drag.moved;
    drag = null;
    if (moved || e.type === 'pointercancel') return;
    const [x, y] = local(e);
    const n = pick(x, y);
    if (n) {
      clickNode(n);
      showTip(n, x, y);
    }
  };
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);
  canvas.addEventListener('pointerleave', () => {
    hover = null;
    tip.hidden = true;
    dirty();
  });
  canvas.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    const [x, y] = local(e);
    const n = pick(x, y);
    if (n) refundNode(n);
  });
  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const [x, y] = local(e);
      anim = null;
      zoomBy(Math.exp(-e.deltaY * 0.0015), x, y);
    },
    { passive: false },
  );

  function showTip(n: GridNode | null, x: number, y: number): void {
    if (!n || !view) {
      tip.hidden = true;
      return;
    }
    const t = tooltipFor(view, n, plan);
    tip.innerHTML = '';
    const head = el('div', 'kgrid-tip-head', tip);
    const ic = el('canvas', `kgrid-tip-icon kgrid-t-${n.type}`, head) as HTMLCanvasElement;
    const px = n.type === 'keystone' || n.type === 'gate' ? 64 : 54;
    ic.width = ic.height = px * dpr;
    ic.style.width = ic.style.height = `${px}px`;
    const ig = ic.getContext('2d')!;
    ig.setTransform(dpr, 0, 0, dpr, 0, 0);
    art.draw(ig, n, lookOf(nodeState(view, n, plan)), px / 2, px / 2, fitRadius(n, px), dpr);
    const ht = el('div', 'kgrid-tip-headtext', head);
    el('div', `kgrid-tip-title kgrid-t-${n.type}`, ht, t.title);
    el('div', 'kgrid-tip-kind', ht, t.kind);
    for (const l of t.lines) el('div', 'kgrid-tip-line', tip, l);
    if (t.flavour) el('div', 'kgrid-tip-flavour', tip, t.flavour);
    if (t.note) el('div', 'kgrid-tip-note', tip, t.note);
    tip.hidden = false;
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    tip.style.left = `${Math.min(w - tw - 8, x + 18)}px`;
    tip.style.top = `${Math.max(8, Math.min(h - th - 8, y + 14))}px`;
  }

  input.addEventListener('input', () => {
    matches = searchGrid(input.value);
    matchAt = 0;
    searchCount.textContent = input.value.trim() ? `${matches.length} star${matches.length === 1 ? '' : 's'}` : '';
    dirty();
  });
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter' && matches.length) {
      const n = grid.byId.get(matches[matchAt % matches.length]!)!;
      matchAt++;
      moveTo({ x: n.x, y: n.y, zoom: Math.max(cam.zoom, 1.1) }, true);
    }
    if (e.key === 'Escape') {
      input.value = '';
      matches = [];
      searchCount.textContent = '';
      input.blur();
      dirty();
    }
  });

  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => resize()) : null;
  ro?.observe(body);
  window.addEventListener('resize', resize);
  const unsubscribe = store.subscribe(() => syncHud());
  queueMicrotask(() => {
    resize();
    syncHud();
  });
  syncHud();
  raf = requestAnimationFrame(frame);

  // Test / screenshot hook (debug builds use it to stage a plan): window.__keystone.grid.
  const hook = { plan: (ids: string[]) => setPlan(ids), focus: (id: string, zoom = 1.2) => { const n = grid.byId.get(id); tip.hidden = true; if (n) moveTo({ x: n.x, y: n.y, zoom }, false); }, hover: (id: string) => { const n = grid.byId.get(id); if (!n) return; const [x, y] = toScreen(n.x, n.y); hover = n; showTip(n, x, y); dirty(); }, whole: () => fitAll(), perf: () => ({ drawMs: Math.round(drawMs * 100) / 100, zoom: cam.zoom }), search: (q: string) => { input.value = q; input.dispatchEvent(new Event('input')); } };
  (window as unknown as { __keystoneGrid?: typeof hook }).__keystoneGrid = hook;

  return {
    el: f.el,
    dispose() {
      cancelAnimationFrame(raf);
      art.dispose();
      ro?.disconnect();
      window.removeEventListener('resize', resize);
      unsubscribe();
      delete (window as unknown as { __keystoneGrid?: unknown }).__keystoneGrid;
    },
    onBack() {
      // Esc / back: drop the plan first, then leave.
      if (plan.length) {
        setPlan([]);
        return true;
      }
      return false;
    },
  };
};

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);


/** How a node state is graded on the sky. */
function lookOf(st: NodeState): Look {
  switch (st) {
    case 'own-gate':
    case 'allocated':
      return 'lit';
    case 'open':
      return 'open';
    case 'planned':
      return 'plan';
    case 'available':
      return 'avail';
    case 'sealed':
      return 'sealed';
    case 'openable':
      return 'openable';
    case 'needs-key':
      return 'needkey';
    case 'dormant':
      return 'dormant';
    default:
      return 'idle';
  }
}
